export const HIGHLIGHT_COLORS = ["#f7e017", "#ff3b30", "#34c759", "#007aff"];

export const HIGHLIGHT_TOOLS = Object.freeze({
  NONE: "none",
  PEN: "pen",
  HIGHLIGHTER: "highlighter",
  LASER: "laser",
  LINE: "line",
  RECTANGLE: "rectangle",
  ELLIPSE: "ellipse",
  ARROW: "arrow",
});

export const HIGHLIGHT_THICKNESS = Object.freeze({
  s: { pen: 3, highlighter: 16 },
  m: { pen: 5, highlighter: 24 },
  l: { pen: 8, highlighter: 36 },
});

const MIN_SHAPE = 0.008;

export const canUseScreenHighlight = (userType) =>
  userType === "teacher" || userType === "associate";

export function isShapeTool(tool) {
  return (
    tool === HIGHLIGHT_TOOLS.LINE ||
    tool === HIGHLIGHT_TOOLS.RECTANGLE ||
    tool === HIGHLIGHT_TOOLS.ELLIPSE ||
    tool === HIGHLIGHT_TOOLS.ARROW
  );
}

export function pointerToNormalized(clientX, clientY, element) {
  if (!element) return { x: 0, y: 0 };
  const rect = element.getBoundingClientRect();
  const width = rect.width || 1;
  const height = rect.height || 1;
  return {
    x: Math.min(1, Math.max(0, (clientX - rect.left) / width)),
    y: Math.min(1, Math.max(0, (clientY - rect.top) / height)),
  };
}

export function pointsToSvgPath(points, width, height) {
  if (!points?.length) return "";
  const toX = (p) => p.x * width;
  const toY = (p) => p.y * height;
  if (points.length === 1) {
    const x = toX(points[0]);
    const y = toY(points[0]);
    return `M ${x} ${y} L ${x} ${y}`;
  }
  let d = `M ${toX(points[0])} ${toY(points[0])}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const midX = (toX(points[i]) + toX(points[i + 1])) / 2;
    const midY = (toY(points[i]) + toY(points[i + 1])) / 2;
    d += ` Q ${toX(points[i])} ${toY(points[i])} ${midX} ${midY}`;
  }
  const last = points[points.length - 1];
  d += ` L ${toX(last)} ${toY(last)}`;
  return d;
}

export function strokeWidthFor(tool, thickness) {
  const sizes = HIGHLIGHT_THICKNESS[thickness] || HIGHLIGHT_THICKNESS.m;
  if (tool === HIGHLIGHT_TOOLS.HIGHLIGHTER) return sizes.highlighter;
  return sizes.pen;
}

export function createStrokeId() {
  return `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function constrainEnd(start, end, tool, size) {
  if (!start || !end) return end;
  const width = size?.width || 1;
  const height = size?.height || 1;
  const x1 = start.x * width;
  const y1 = start.y * height;
  const x2 = end.x * width;
  const y2 = end.y * height;
  const dx = x2 - x1;
  const dy = y2 - y1;

  if (tool === HIGHLIGHT_TOOLS.RECTANGLE || tool === HIGHLIGHT_TOOLS.ELLIPSE) {
    const side = Math.max(Math.abs(dx), Math.abs(dy));
    return {
      x: (x1 + side * Math.sign(dx || 1)) / width,
      y: (y1 + side * Math.sign(dy || 1)) / height,
    };
  }

  const angle = Math.atan2(dy, dx);
  const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
  const len = Math.hypot(dx, dy);
  return {
    x: (x1 + Math.cos(snapped) * len) / width,
    y: (y1 + Math.sin(snapped) * len) / height,
  };
}

function arrowPath(x1, y1, x2, y2, strokeWidth = 5) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const head = Math.min(Math.max(14, strokeWidth * 3.2), len * 0.45);
  const angle = Math.atan2(dy, dx);
  const p1x = x2 - head * Math.cos(angle - Math.PI / 6);
  const p1y = y2 - head * Math.sin(angle - Math.PI / 6);
  const p2x = x2 - head * Math.cos(angle + Math.PI / 6);
  const p2y = y2 - head * Math.sin(angle + Math.PI / 6);
  return `M ${x1} ${y1} L ${x2} ${y2} M ${p1x} ${p1y} L ${x2} ${y2} L ${p2x} ${p2y}`;
}

export function getShapeGeometry(stroke, width, height) {
  if (!isShapeTool(stroke?.tool) || !stroke.points?.length) return null;
  const start = stroke.points[0];
  const rawEnd = stroke.points[stroke.points.length - 1] || start;
  const end = stroke.constrain
    ? constrainEnd(start, rawEnd, stroke.tool, { width, height })
    : rawEnd;
  const x1 = start.x * width;
  const y1 = start.y * height;
  const x2 = end.x * width;
  const y2 = end.y * height;

  if (stroke.tool === HIGHLIGHT_TOOLS.LINE) {
    return { type: "line", x1, y1, x2, y2 };
  }
  if (stroke.tool === HIGHLIGHT_TOOLS.ARROW) {
    return { type: "path", d: arrowPath(x1, y1, x2, y2, stroke.width) };
  }

  const x = Math.min(x1, x2);
  const y = Math.min(y1, y2);
  const w = Math.abs(x2 - x1);
  const h = Math.abs(y2 - y1);
  if (stroke.tool === HIGHLIGHT_TOOLS.RECTANGLE) {
    return { type: "rect", x, y, width: w, height: h };
  }
  if (stroke.tool === HIGHLIGHT_TOOLS.ELLIPSE) {
    return {
      type: "ellipse",
      cx: x + w / 2,
      cy: y + h / 2,
      rx: w / 2,
      ry: h / 2,
    };
  }
  return null;
}

export function isTinyShape(stroke) {
  if (!isShapeTool(stroke?.tool) || !stroke.points?.length) return false;
  const start = stroke.points[0];
  const end = stroke.points[stroke.points.length - 1];
  const dx = Math.abs(start.x - end.x);
  const dy = Math.abs(start.y - end.y);
  if (
    stroke.tool === HIGHLIGHT_TOOLS.LINE ||
    stroke.tool === HIGHLIGHT_TOOLS.ARROW
  ) {
    return Math.hypot(dx, dy) < MIN_SHAPE;
  }
  return dx < MIN_SHAPE && dy < MIN_SHAPE;
}
